import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Icon } from '@/components/icons';
import { useAuth } from '@/hooks/useAuth';
import { CARD_SECTION_CLASS } from '@/styles/classes';

const headerBtn =
  'inline-flex items-center rounded-md p-2 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50';

const menuItemClass =
  'flex w-full items-center gap-3 px-4 py-2.5 text-left text-base text-unilink-text-primary hover:bg-unilink-surface-hover';

export function SettingsMenu() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const isStaff = user?.is_staff === true;

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) {
        return;
      }
      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className={headerBtn}
        title={t('common.settings')}
        aria-label={t('common.settings')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
      >
        <Icon icon="gear" className="size-5" />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label={t('common.settings')}
          className={clsx(
            'absolute right-0 z-50 mt-2 min-w-[220px] py-1',
            CARD_SECTION_CLASS
          )}
        >
          <Link
            role="menuitem"
            to="/account-settings"
            className={menuItemClass}
            onClick={() => setOpen(false)}
          >
            <Icon icon="user" className="size-6 text-blue-navbar" />
            <span>{t('account.title')}</span>
          </Link>
          {isStaff ? (
            <Link
              role="menuitem"
              to="/establishment-settings"
              className={menuItemClass}
              onClick={() => setOpen(false)}
            >
              <Icon icon="building" className="size-6 text-blue-navbar" />
              <span>{t('account.establishmentSettingsTitle')}</span>
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
